import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IOption, IRequestFlags, VehicleBody } from 'modules/core/types';
import {
  getDefaultVehiclePhotoThunk,
  getVehicleAdjustRetailThunk,
  getVehicleBodiesThunk,
  getVehicleBodyTypesThunk,
  getVehicleMakesThunk,
  getVehicleAdjustmentLogThunk,
  getVehicleModelsThunk,
  createUserVehicleThunk,
  updateUserVehicleThunk,
  getBalloonMaxOdometersThunk,
  deleteUserVehicleThunk,
} from 'actions/vehicleActions';
import { IVehicleDetails } from 'components/Modals/VehicleDetailsDialog/types';
import { IVehicleAdjustRetailPayload, IVehicleLogPayload } from 'components/Modals/VehicleAdjustmentDialog/types';
import { IVehicleModel } from 'modules/core/components/VehicleFilters/types';
import { getPassengerCapacitiesThunk } from 'actions/passengerCapacitiesAction';
import { ICreateUserVehicle } from 'components/Modals/AddVehicleDialog/types';
import { CREATE_USER_VEHICLE_INITIAL_VALUES } from 'components/Modals/AddVehicleDialog/consts/consts';
import { TableName } from 'modules/core/enums';

export interface IVehicleReducerState {
  vehicleBodyTypes: IOption[];
  vehicleMakes: IOption[];
  vehicleModels: IVehicleModel[];
  bodies: VehicleBody[];
  passengerCapacities: number[];
  adjustmentId: number;
  vehicleAdjustRetail: IVehicleAdjustRetailPayload[];
  vehicleAdjustmentLogs: IVehicleLogPayload[];
  vehicleDetails?: IVehicleDetails;
  defaultVehiclePhoto: string;
  addUserVehicleFlags: IRequestFlags;
  updateUserVehicleFlags: IRequestFlags;
  deleteUserVehicleFlags: IRequestFlags;
  createdUserVehicle: ICreateUserVehicle;
  createdUserVehicleTableName: TableName | null;
  createdUserVehicleLenderId: string | null;
  balloonMaxOdometers: Record<string, number>;
  addedIsUserVehicle: boolean;
  isDealSourceVehicle: boolean;
}

const initialState: IVehicleReducerState = {
  vehicleBodyTypes: [],
  vehicleMakes: [],
  vehicleModels: [],
  bodies: [],
  passengerCapacities: [],
  adjustmentId: 0,
  vehicleAdjustRetail: [],
  vehicleAdjustmentLogs: [],
  defaultVehiclePhoto: '',
  addUserVehicleFlags: {
    isInvalid: false,
    isFetching: false,
    isLoaded: false,
  },
  updateUserVehicleFlags: {
    isInvalid: false,
    isFetching: false,
    isLoaded: false,
  },
  deleteUserVehicleFlags: {
    isInvalid: false,
    isFetching: false,
    isLoaded: false,
  },
  createdUserVehicle: CREATE_USER_VEHICLE_INITIAL_VALUES,
  createdUserVehicleTableName: null,
  createdUserVehicleLenderId: null,
  balloonMaxOdometers: {},
  addedIsUserVehicle: false,
  isDealSourceVehicle: false,
};

export const vehicleSlice = createSlice({
  name: 'vehicle',
  initialState,
  reducers: {
    setVehicleDetails: (state: IVehicleReducerState, { payload: vehicleDetails }: PayloadAction<IVehicleDetails>) => ({
      ...state,
      vehicleDetails,
    }),
    clearVehicleDetails: (state: IVehicleReducerState) => ({
      ...state,
      vehicleDetails: initialState.vehicleDetails,
    }),
    setAdjustmentId: (state: IVehicleReducerState, { payload: adjustmentId }: PayloadAction<number>) => ({
      ...state,
      adjustmentId,
    }),
    setCreatedUserVehicleTableName: (state: IVehicleReducerState, { payload }: PayloadAction<TableName | null>) => ({
      ...state,
      createdUserVehicleTableName: payload,
    }),
    setCreatedUserVehicleLenderId: (state: IVehicleReducerState, { payload }: PayloadAction<string | null>) => ({
      ...state,
      createdUserVehicleLenderId: payload,
    }),
    resetAddUserVehicleFlags: (state: IVehicleReducerState) => ({
      ...state,
      addUserVehicleFlags: {
        isInvalid: false,
        isFetching: false,
        isLoaded: false,
      },
    }),
    resetUpdateUserVehicleFlags: (state: IVehicleReducerState) => ({
      ...state,
      updateUserVehicleFlags: {
        isInvalid: false,
        isFetching: false,
        isLoaded: false,
      },
    }),
    resetCreatedUserVehicle: (state: IVehicleReducerState) => ({
      ...state,
      createdUserVehicle: CREATE_USER_VEHICLE_INITIAL_VALUES,
      createdUserVehicleLenderId: null,
    }),
    clearVehicleAdjustmentData: (state: IVehicleReducerState) => ({
      ...state,
      adjustmentId: initialState.adjustmentId,
      vehicleAdjustRetail: initialState.vehicleAdjustRetail,
      vehicleAdjustmentLogs: initialState.vehicleAdjustmentLogs,
    }),
    setAddedIsUserVehicle: (state: IVehicleReducerState, { payload }: PayloadAction<boolean>) => ({
      ...state,
      addedIsUserVehicle: payload,
    }),
    resetAddedIsUserVehicle: (state: IVehicleReducerState) => ({
      ...state,
      addedIsUserVehicle: false,
    }),
    setIsDealSourceVehicle: (state: IVehicleReducerState, { payload }: PayloadAction<boolean>) => ({
      ...state,
      isDealSourceVehicle: payload,
    }),
    resetIsDealSourceVehicle: (state: IVehicleReducerState) => ({
      ...state,
      isDealSourceVehicle: false,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(
      getVehicleBodyTypesThunk.fulfilled,
      (state: IVehicleReducerState, { payload: vehicleBodyTypes }: PayloadAction<IOption[]>) => ({
        ...state,
        vehicleBodyTypes,
      }),
    );
    builder.addCase(
      getPassengerCapacitiesThunk.fulfilled,
      (state: IVehicleReducerState, { payload: passengerCapacities }: PayloadAction<number[]>) => ({
        ...state,
        passengerCapacities,
      }),
    );
    builder.addCase(
      getDefaultVehiclePhotoThunk.fulfilled,
      (state: IVehicleReducerState, { payload }: PayloadAction<string>) => ({
        ...state,
        defaultVehiclePhoto: payload,
      }),
    );
    builder.addCase(
      getVehicleMakesThunk.fulfilled,
      (state: IVehicleReducerState, { payload: vehicleMakes }: PayloadAction<IOption[]>) => ({
        ...state,
        vehicleMakes,
      }),
    );

    builder.addCase(
      getVehicleModelsThunk.fulfilled,
      (state: IVehicleReducerState, { payload: vehicleModels }: PayloadAction<IVehicleModel[]>) => ({
        ...state,
        vehicleModels,
      }),
    );

    builder.addCase(
      getVehicleBodiesThunk.fulfilled,
      (state: IVehicleReducerState, { payload: bodies }: PayloadAction<VehicleBody[]>) => ({
        ...state,
        bodies,
      }),
    );

    builder.addCase(
      getVehicleAdjustRetailThunk.fulfilled,
      (
        state: IVehicleReducerState,
        { payload: vehicleAdjustRetail }: PayloadAction<IVehicleAdjustRetailPayload[]>,
      ) => ({
        ...state,
        vehicleAdjustRetail,
      }),
    );

    builder.addCase(
      getVehicleAdjustmentLogThunk.fulfilled,
      (state: IVehicleReducerState, { payload: vehicleAdjustmentLogs }: PayloadAction<IVehicleLogPayload[]>) => ({
        ...state,
        vehicleAdjustmentLogs,
      }),
    );

    builder.addCase(
      createUserVehicleThunk.fulfilled,
      (state: IVehicleReducerState, { payload }: PayloadAction<ICreateUserVehicle>) => ({
        ...state,
        createdUserVehicle: payload,
        addUserVehicleFlags: {
          isInvalid: false,
          isFetching: false,
          isLoaded: true,
        },
      }),
    );
    builder.addCase(createUserVehicleThunk.pending, (state: IVehicleReducerState) => ({
      ...state,
      addUserVehicleFlags: {
        isInvalid: false,
        isFetching: true,
        isLoaded: false,
      },
    }));
    builder.addCase(createUserVehicleThunk.rejected, (state: IVehicleReducerState) => ({
      ...state,
      addUserVehicleFlags: {
        isInvalid: true,
        isFetching: false,
        isLoaded: true,
      },
    }));

    builder.addCase(
      updateUserVehicleThunk.fulfilled,
      (state: IVehicleReducerState, { payload }: PayloadAction<ICreateUserVehicle>) => ({
        ...state,
        createdUserVehicle: payload,
        updateUserVehicleFlags: {
          isInvalid: false,
          isFetching: false,
          isLoaded: true,
        },
      }),
    );
    builder.addCase(updateUserVehicleThunk.pending, (state: IVehicleReducerState) => ({
      ...state,
      updateUserVehicleFlags: {
        isInvalid: false,
        isFetching: true,
        isLoaded: false,
      },
    }));
    builder.addCase(updateUserVehicleThunk.rejected, (state: IVehicleReducerState) => ({
      ...state,
      updateUserVehicleFlags: {
        isInvalid: true,
        isFetching: false,
        isLoaded: true,
      },
    }));
    builder.addCase(deleteUserVehicleThunk.fulfilled, (state: IVehicleReducerState) => ({
      ...state,
      deleteUserVehicleFlags: {
        isInvalid: false,
        isFetching: false,
        isLoaded: true,
      },
    }));
    builder.addCase(deleteUserVehicleThunk.pending, (state: IVehicleReducerState) => ({
      ...state,
      deleteUserVehicleFlags: {
        isInvalid: false,
        isFetching: true,
        isLoaded: false,
      },
    }));
    builder.addCase(deleteUserVehicleThunk.rejected, (state: IVehicleReducerState) => ({
      ...state,
      deleteUserVehicleFlags: {
        isInvalid: true,
        isFetching: false,
        isLoaded: true,
      },
    }));
    builder.addCase(
      getBalloonMaxOdometersThunk.fulfilled,
      (state: IVehicleReducerState, { payload: balloonMaxOdometers }: PayloadAction<Record<string, number>>) => ({
        ...state,
        balloonMaxOdometers,
      }),
    );
  },
});

export const {
  setVehicleDetails,
  setAdjustmentId,
  setCreatedUserVehicleTableName,
  setCreatedUserVehicleLenderId,
  clearVehicleAdjustmentData,
  clearVehicleDetails,
  resetAddUserVehicleFlags,
  resetUpdateUserVehicleFlags,
  resetCreatedUserVehicle,
  setAddedIsUserVehicle,
  resetAddedIsUserVehicle,
  setIsDealSourceVehicle,
  resetIsDealSourceVehicle,
} = vehicleSlice.actions;
